/* istanbul ignore file */
import React from 'react';
import type { CookieObj } from '@noths/polaris-client-gdpr-compliance';
import { acceptsFunctionalCookies } from '@noths/polaris-client-gdpr-compliance';
import { isContentPreviewEnabled } from '@noths/polaris-client-next-components';
import { PreviewHeader } from '@noths/polaris-client-ribbons-design-system';
import type { SiteChromeContent } from '@noths/polaris-client-site-chrome-ui';
import { fetchSiteChromeContent } from '@noths/polaris-client-site-chrome-ui';
import { getMvtStrategyFromRequest } from '@noths/polaris-client-user-configuration';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import getConfig from 'next/config';
import Head from 'next/head';

import { PartnerStorefrontLayout } from 'src/components/layouts/PartnerStorefrontLayout/PartnerStorefrontLayout';
import { SKIP_CONTENT_TARGET_ID } from 'src/constants/elementIds';
import { getFeatureFlagsWithMatchers } from 'src/featureFlags/getFeatureFlagsWithMatchers';
import { fetchPartnerContent } from 'src/services/contentstack/fetchPartnerContent';
import { getContentstackEnvironment } from 'src/services/contentstack/getContentstackEnvironment';
import type { Partner } from 'src/services/contentstack/types/PartnerPage.types';
import { logger } from 'src/utils/serverLogger';

const {
  publicRuntimeConfig,
  serverRuntimeConfig: { CLEAR_PREVIEW_API_URL },
} = getConfig();

interface PageMetadata {
  currency: CurrencyCode;
  featureFlags: Record<string, unknown>;
  functionalCookiesConsented?: boolean;
  isPreview: boolean;
  pathname: string;
}

interface ContentPageProps {
  pageConfig: {
    contentType: string;
    env: string;
    pageType: string;
    trackingCategory: string;
  };
  pageEventCategory: string;
  pageMetadata: PageMetadata;
  partnerContent?: Partner;
  siteChromeContent: SiteChromeContent | null;
}

const pageConfig = {
  env: process.env.NODE_ENV,
  contentType: 'partner',
  pageType: 'partner',
  trackingCategory: 'Partner',
};

export const getServerSideProps: GetServerSideProps<ContentPageProps> = async (context) => {
  const {
    query: { shortcode },
    req,
  } = context;
  const preview = isContentPreviewEnabled(req);
  const env = getContentstackEnvironment(preview);
  const partnerShortcode = Array.isArray(shortcode) ? shortcode[0]! : shortcode!;

  const pathname = req.url?.split('?')[0] ?? '/';
  const featureFlags = getFeatureFlagsWithMatchers(req);
  const currency = (req.cookies.currency as CurrencyCode) || 'GBP';

  const pageMetadata: PageMetadata = {
    currency,
    featureFlags,
    functionalCookiesConsented: acceptsFunctionalCookies(req.cookies as CookieObj),
    isPreview: preview,
    pathname,
  };

  const navContentVariantId = (featureFlags as Record<string, string>).nav_content_experiment;

  try {
    const [partnerContent, siteChromeContent] = await Promise.all([
      fetchPartnerContent({
        shortcode: partnerShortcode,
        env,
      }),
      fetchSiteChromeContent({ env, logger }, navContentVariantId).catch(() => null),
    ]);

    // Return 404 if partner content is not found
    if (!partnerContent) {
      return {
        notFound: true,
      };
    }

    const pageEventData = {
      app_version: publicRuntimeConfig.appVersion,
      bot_caching: getMvtStrategyFromRequest(req) === 'bot' ? 'true' : 'false',
      category: 'Partner Storefront',
      environment: process.env.NODE_ENV,
      react: true,
      release_version: publicRuntimeConfig.version,
    };

    return {
      props: {
        pageConfig,
        pageEventData,
        pageMetadata,
        partnerContent,
        pageEventCategory: pageConfig.trackingCategory,
        siteChromeContent: siteChromeContent as SiteChromeContent | null,
      },
    };
  } catch (error) {
    logger.error(`Error fetching partner page for ${partnerShortcode}`, {
      ...(error instanceof Error
        ? { message: error.message, stack: error.stack }
        : { message: 'unknown error' }),
    });
    return {
      notFound: true,
    };
  }
};

const PartnersPage = ({
  pageMetadata,
  partnerContent,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  return (
    <>
      <Head>
        <title>
          {partnerContent ? `${partnerContent.name} | ` : ''}Storefront | notonthehighstreet.com
        </title>
        <meta content="noindex, follow" name="robots" />
      </Head>
      <div id={SKIP_CONTENT_TARGET_ID}>
        {pageMetadata.isPreview && <PreviewHeader href={CLEAR_PREVIEW_API_URL} />}
        {partnerContent && <PartnerStorefrontLayout partner={partnerContent} />}
      </div>
    </>
  );
};

export default PartnersPage;
